// src/components/CartDrawer.jsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { FiX, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";

const eur = (n) => `${n.toFixed(2)} €`;

export default function CartDrawer() {
  const { items, subtotal, isOpen, closeCart, setQty, remove } = useCart();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeCart();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeCart]);

  const goToCheckout = () => {
    closeCart();
    navigate("/porosia");
  };

  return (
    <>
      <Overlay $open={isOpen} onClick={closeCart} aria-hidden={!isOpen} />

      <Panel $open={isOpen} aria-hidden={!isOpen} aria-label={t("cart.title")}>
        <Head>
          <h3>{t("cart.title")}</h3>
          <Close onClick={closeCart} aria-label={t("common.close")}>
            <FiX />
          </Close>
        </Head>

        {items.length === 0 ? (
          <Empty>
            <p>{t("cart.empty")}</p>
            <LinkButton onClick={goToProducts(closeCart, navigate)}>
              {t("cart.seeProducts")}
            </LinkButton>
          </Empty>
        ) : (
          <>
            <List>
              {items.map((line) => (
                <Line key={line.id}>
                  <Swatch $hex={line.hex}>
                    {line.image && (
                      <img src={line.image} alt="" loading="lazy" />
                    )}
                  </Swatch>

                  <Info>
                    <Name>{line.nameSq}</Name>
                    <Code>{line.pmuCode}</Code>

                    <Row>
                      <Stepper>
                        <StepBtn
                          onClick={() => setQty(line.id, line.qty - 1)}
                          disabled={line.qty <= 1}
                          aria-label={t("product.decrease")}
                        >
                          <FiMinus />
                        </StepBtn>
                        <Qty>{line.qty}</Qty>
                        <StepBtn
                          onClick={() => setQty(line.id, line.qty + 1)}
                          disabled={line.qty >= line.stock}
                          aria-label={t("product.increase")}
                        >
                          <FiPlus />
                        </StepBtn>
                      </Stepper>

                      <Price>{eur(line.price * line.qty)}</Price>
                    </Row>

                    {line.qty >= line.stock && (
                      <Note>{t("cart.maxStock", { count: line.stock })}</Note>
                    )}
                  </Info>

                  <Remove
                    onClick={() => remove(line.id)}
                    aria-label={t("cart.remove")}
                  >
                    <FiTrash2 />
                  </Remove>
                </Line>
              ))}
            </List>

            <Foot>
              <Subtotal>
                <span>{t("cart.total")}</span>
                <strong>{eur(subtotal)}</strong>
              </Subtotal>
              <Hint>{t("cart.shippingNote")}</Hint>
              <Checkout onClick={goToCheckout}>{t("cart.checkout")}</Checkout>
            </Foot>
          </>
        )}
      </Panel>
    </>
  );
}

const goToProducts = (closeCart, navigate) => () => {
  closeCart();
  navigate("/produktet");
};

/* ------------------------------------------------------------------ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(14, 13, 16, 0.45);
  transition:
    opacity 0.3s ${({ theme }) => theme.ease.out},
    visibility 0.3s;

  ${({ $open }) =>
    $open
      ? css`
          opacity: 1;
          visibility: visible;
        `
      : css`
          opacity: 0;
          visibility: hidden;
        `}
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 201;
  width: min(420px, 100vw);
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.bg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  transition:
    transform 0.42s ${({ theme }) => theme.ease.out},
    visibility 0.42s;

  ${({ $open }) =>
    $open
      ? css`
          transform: translateX(0);
          visibility: visible;
        `
      : css`
          transform: translateX(100%);
          visibility: hidden;
        `}
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};

  h3 {
    font-size: 1.3rem;
  }
`;

const Close = styled.button`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 1.1rem;
  color: ${({ theme }) => theme.color.ink};

  &:hover {
    background: ${({ theme }) => theme.color.bgSoft};
  }
`;

const Empty = styled.div`
  flex: 1;
  display: grid;
  place-content: center;
  gap: 14px;
  text-align: center;
  color: ${({ theme }) => theme.color.muted};
`;

const LinkButton = styled.button`
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const List = styled.ul`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px;
`;

const Line = styled.li`
  display: grid;
  grid-template-columns: 62px 1fr auto;
  gap: 14px;
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const Swatch = styled.div`
  width: 62px;
  height: 62px;
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  background: ${({ $hex, theme }) => $hex || theme.color.bgSoft};
  border: 1px solid rgba(14, 13, 16, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8%;
  }
`;

const Info = styled.div`
  min-width: 0;
`;

const Name = styled.p`
  margin: 0;
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  font-size: ${({ theme }) => theme.size.sm};
`;

const Code = styled.p`
  margin: 2px 0 10px;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  letter-spacing: 0.04em;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.line};
  border-radius: ${({ theme }) => theme.radius.pill};
  overflow: hidden;
`;

const StepBtn = styled.button`
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.color.ink};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.bgSoft};
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Qty = styled.span`
  min-width: 26px;
  text-align: center;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const Price = styled.span`
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  font-size: ${({ theme }) => theme.size.sm};
`;

const Note = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.warn};
`;

const Remove = styled.button`
  align-self: start;
  color: ${({ theme }) => theme.color.muted};
  font-size: 0.95rem;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.color.danger};
  }
`;

const Foot = styled.div`
  padding: 20px 24px 26px;
  border-top: 1px solid ${({ theme }) => theme.color.line};
  background: ${({ theme }) => theme.color.bgSoft};
`;

const Subtotal = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: ${({ theme }) => theme.color.ink};

  strong {
    font-family: ${({ theme }) => theme.font.display};
    font-size: 1.4rem;
  }
`;

const Hint = styled.p`
  margin: 6px 0 16px;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
`;

const Checkout = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.ink};
  color: #fff;
  font-weight: ${({ theme }) => theme.weight.semi};
  font-size: ${({ theme }) => theme.size.sm};
  letter-spacing: 0.02em;
  transition: background 0.25s;

  &:hover {
    background: ${({ theme }) => theme.color.brand};
  }
`;
